import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {LoadingController} from '@ionic/angular';
import {CacheService} from './cache.service';
import {map, switchMap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of, throwError, zip} from 'rxjs';
import {LoginReq} from '../models/login/loginReq';
import {LoginRes} from '../models/login/loginRes';
import {environment} from '../../environments/environment';
import {CalendarRes} from '../models/calendar/calendarRes';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  users = null;
  loginUrl = `${environment.apiUrl}/LoginServlet`;
  isEditableBe$ = new BehaviorSubject(true);

  constructor(
    private activatedRoute: ActivatedRoute,
    private loadingController: LoadingController,
    private cacheService: CacheService,
    private http: HttpClient,
    private router: Router
  ) {
  }

  private getDeviceInfo(): Observable<[{name: string; operatingSystem: string; osVersion: string; platform: string; model: string}, {uuid: string}]> {
    let uuid = localStorage.getItem('deviceUuid');
    if (!uuid) {
      const cryptoAny = crypto as any;
      uuid = (typeof crypto !== 'undefined' && cryptoAny.randomUUID)
        ? cryptoAny.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('deviceUuid', uuid);
    }
    return of([
      {
        name: navigator.platform ?? 'web',
        operatingSystem: 'web',
        osVersion: navigator.userAgent,
        platform: 'web',
        model: ''
      },
      {uuid}
    ]);
  }

  getAuthData(loginName: string, loginPassword: string): Observable<LoginRes | null> {
    return this.getDeviceInfo().pipe(
      switchMap(deviceInfo => {
        // Use the previously saved auth token as deviceToken so the backend
        // can match this device. Falls back to the UUID on first ever login.
        const savedDeviceToken = localStorage.getItem('deviceAuthToken') ?? deviceInfo[1].uuid;
        const login: LoginReq = {
          loginName,
          loginPassword,
          notificationToken: localStorage.getItem('notificationToken') ?? 'empty',
          deviceToken: savedDeviceToken,
          appName: 'PHILIPIAK',
          deviceName: deviceInfo[0].name + ' uuid: ' + deviceInfo[1].uuid,
          deviceOs: deviceInfo[0].operatingSystem + ' ' + deviceInfo[0].osVersion
        };
        return this.http.post(this.loginUrl, login).pipe(
          switchMap(result => {
            if (!result) { return of(null); }
            login.loginPassword = '';
            // Persist the auth token so it can be sent as deviceToken next login
            const loginRes = result as LoginRes;
            if (loginRes.token) {
              localStorage.setItem('deviceAuthToken', loginRes.token);
            }
            this.cacheService.cacheRequests(this.loginUrl, result, 1000000000).subscribe();
            return of(loginRes);
          })
        );
      })
    );
  }

  deleteAuthData(): Observable<any> {
    this.router.navigate(['/login']);
    localStorage.removeItem('deviceAuthToken');
    this.cacheService.clearCacheData();
    return this.cacheService.invalidateCacheEntry(this.loginUrl);
  }

  isLoggedIn(): Observable<boolean> {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => of(!!result?.data?.token))
    );
  }

  getToken(): Observable<string> {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => {
        const token = result?.data?.token;
        if (!token) {
          return throwError({msg: 'Unauthorized', status: 401});
        }
        return of(token);
      })
    );
  }

  getIntrauser(): Observable<LoginRes | null> {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => of(result?.data?.token ? result.data : null))
    );
  }

  getIntrauserId(): Observable<any> {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => of(result?.data?.token ? result.data.intrauser_id : null))
    );
  }

  setSelectedSubjectId(subjectId: any): Observable<any> {
    return this.cacheService.cacheRequests('selectedSubject', subjectId, 1000000000);
  }

  getSelectedSubjectId(): Observable<any> {
    return this.cacheService.getCachedRequest('selectedSubject').pipe(
      switchMap(result => of(result?.data ?? null))
    );
  }

  isEditable(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId()
    ).pipe(
      switchMap(ids => this.isEditableBe$.pipe(
        switchMap(isEditableBe => {
          if (Number(ids[0]) === Number(ids[1]) && isEditableBe) {
            if (calendar) {
              const dateParts = calendar.date.split('.');
              const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
              const actDate = new Date();
              actDate.setHours(0, 0, 0, 0);
              return of(actDate <= calendarDate);
            }
            return of(true);
          }
          return of(false);
        })
      ))
    );
  }

  isEditableOrders(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId()
    ).pipe(
      switchMap(ids => this.isEditableBe$.pipe(
        switchMap(isEditableBe => {
          if (Number(ids[0]) === Number(ids[1]) && isEditableBe) {
            if (calendar) {
              if (Number(calendar.calendar_state_id) === 19341) { return of(false); }
              const dateParts = calendar.date.split('.');
              const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
              const actDate = new Date();
              actDate.setHours(0, 0, 0, 0);
              if (actDate > calendarDate) {
                const yesterday = new Date(actDate);
                yesterday.setDate(yesterday.getDate() - 1);
                return of(yesterday.toDateString() === calendarDate.toDateString() && new Date().getHours() < 7);
              }
              return of(true);
            }
            return of(true);
          }
          return of(false);
        })
      ))
    );
  }

  isEditableRecommendations(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId()
    ).pipe(
      switchMap(ids => this.isEditableBe$.pipe(
        switchMap(isEditableBe => {
          if (Number(ids[0]) === Number(ids[1]) && isEditableBe) {
            if (calendar) {
              const dateParts = calendar.date.split('.');
              const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]), Number(dateParts[0]));
              calendarDate.setMonth(calendarDate.getMonth() + 1);
              const actDate = new Date();
              actDate.setHours(0, 0, 0, 0);
              return of(actDate <= calendarDate);
            }
            return of(true);
          }
          return of(false);
        })
      ))
    );
  }
}
